/**
 * Hook wrapping @zoom/appssdk: config, getMeetingContext, getUserContext,
 * breakout room events.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import zoomSdk from '@zoom/appssdk';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface ZoomMeetingContext {
  meetingID: string;
  meetingTopic?: string;
}

export interface ZoomUserContext {
  participantId: string;
  screenName: string;
  role: string; // 'host' | 'coHost' | 'attendee'
  participantUUID?: string;
}

export interface ZoomContextState {
  /** True while the SDK is being configured */
  loading: boolean;
  /** Error during SDK init */
  error: string | null;
  /** True once zoomSdk.config() succeeds */
  configured: boolean;
  /** Meeting-level info */
  meeting: ZoomMeetingContext | null;
  /** Current user info */
  user: ZoomUserContext | null;
  /** True if the user is currently in a breakout room */
  inBreakoutRoom: boolean;
  /** True if the current user is the host or co-host */
  isHost: boolean;
  /**
   * Where this app instance is rendered: 'inMeeting' (sidebar),
   * 'inCollaborate' (main window, opened for everyone by startCollaborate),
   * etc. Drives the collaborate display view.
   */
  runningContext: string | null;
}

/* ------------------------------------------------------------------ */
/*  Supported APIs                                                    */
/* ------------------------------------------------------------------ */

// Must stay in sync with zoom-marketplace/manifest.json `capabilities`, and
// every entry must be enabled on the Marketplace app (Features + Embed APIs)
// or the SDK silently won't grant it. config() tolerates listing more than
// is granted, so a superset is safe.
const REQUIRED_APIS = [
  'authorize',
  'onAuthorized',
  'getRunningContext',
  'getSupportedJsApis',
  'getMeetingContext',
  'getUserContext',
  'addEventListener',
  'removeEventListener',
  'sendMessageToChat',
  'sendAppInvitationToAllParticipants',
  'startCollaborate',
  'endCollaborate',
  'showNotification',
  'expandApp',
  'getMeetingParticipants',
  'onParticipantChange',
  'onBreakoutRoomChange',
  'onCollaborateChange',
  'onRunningContextChange',
  'openUrl',
] as Parameters<typeof zoomSdk.config>[0]['capabilities'];

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

export function useZoomContext(): ZoomContextState & {
  refreshUser: () => Promise<void>;
} {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [meeting, setMeeting] = useState<ZoomMeetingContext | null>(null);
  const [user, setUser] = useState<ZoomUserContext | null>(null);
  const [inBreakoutRoom, setInBreakoutRoom] = useState(false);
  const [runningContext, setRunningContext] = useState<string | null>(null);
  const initRef = useRef(false);

  /* -- Initialise SDK -------------------------------------------- */
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    (async () => {
      try {
        // Configure the SDK
        const cfg = await zoomSdk.config({
          capabilities: REQUIRED_APIS,
        });
        setConfigured(true);

        // config()'s runningContext can be stale/incorrect when the app is
        // launched straight into Collaborate; getRunningContext() is the
        // authoritative source, so prefer it and fall back to config's value.
        let ctx = cfg?.runningContext ?? null;
        try {
          const rc = await zoomSdk.getRunningContext();
          if (rc?.context) ctx = rc.context;
        } catch {
          // keep config()'s value
        }
        setRunningContext(ctx);

        // Fetch meeting context
        try {
          const meetingCtx = await zoomSdk.getMeetingContext();
          setMeeting({
            meetingID: meetingCtx.meetingID ?? '',
            meetingTopic: meetingCtx.meetingTopic,
          });
        } catch {
          console.warn('Could not get meeting context');
        }

        // Fetch user context
        try {
          const userCtx = await zoomSdk.getUserContext();
          setUser({
            participantId: userCtx.participantId ?? '',
            screenName: userCtx.screenName ?? '',
            role: userCtx.role ?? 'attendee',
            participantUUID: userCtx.participantUUID,
          });
        } catch {
          console.warn('Could not get user context');
        }

        // Listen for breakout room changes
        try {
          zoomSdk.addEventListener('onMyActiveSpeakerChange', () => {
            // Placeholder: breakout room events
          });
        } catch {
          // Not all SDK versions support this
        }

        setLoading(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to configure Zoom SDK';
        console.error('Zoom SDK config error:', message);
        setError(message);
        setLoading(false);
      }
    })();
  }, []);

  /* -- Breakout room listener ------------------------------------ */
  useEffect(() => {
    if (!configured) return;

    const handleBreakoutChange = (event: { status?: string; breakoutRoomUUID?: string }) => {
      if (event.status === 'joined' && event.breakoutRoomUUID) {
        setInBreakoutRoom(true);
      } else {
        setInBreakoutRoom(false);
      }
    };

    try {
      zoomSdk.addEventListener('onBreakoutRoomChange' as never, handleBreakoutChange);
    } catch {
      // Breakout room events may not be available in all contexts
    }

    return () => {
      try {
        zoomSdk.removeEventListener('onBreakoutRoomChange' as never, handleBreakoutChange);
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [configured]);

  /* -- Running-context listener ---------------------------------- */
  // startCollaborate / endCollaborate move this app instance between the
  // sidebar ('inMeeting') and the shared main window ('inCollaborate'). The
  // context is only read once at config() time, so without this listener the
  // host's view never flips to the display page when they start sharing.
  // Zoom also requires config() to be re-called after a context change to
  // refresh API permissions for the new context.
  useEffect(() => {
    if (!configured) return;

    const handleContextChange = (event: { runningContext?: string }) => {
      if (event.runningContext) setRunningContext(event.runningContext);
      // Re-grant capabilities in the new context; ignore failures (the view
      // switch is driven by runningContext above, not by this call).
      zoomSdk.config({ capabilities: REQUIRED_APIS }).catch(() => {});
    };

    try {
      zoomSdk.addEventListener('onRunningContextChange' as never, handleContextChange);
    } catch {
      // Event may not be available in all SDK versions / contexts
    }

    return () => {
      try {
        zoomSdk.removeEventListener('onRunningContextChange' as never, handleContextChange);
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [configured]);

  /* -- Refresh user context -------------------------------------- */
  const refreshUser = useCallback(async () => {
    if (!configured) return;
    try {
      const userCtx = await zoomSdk.getUserContext();
      setUser({
        participantId: userCtx.participantId ?? '',
        screenName: userCtx.screenName ?? '',
        role: userCtx.role ?? 'attendee',
        participantUUID: userCtx.participantUUID,
      });
    } catch {
      console.warn('Could not refresh user context');
    }
  }, [configured]);

  const isHost = user?.role === 'host' || user?.role === 'coHost';

  return {
    loading,
    error,
    configured,
    meeting,
    user,
    inBreakoutRoom,
    isHost,
    runningContext,
    refreshUser,
  };
}
